import React, { useEffect, useState, useCallback } from "react";
import { useCompany } from "@/context/CompanyContext";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  KeyRound, Copy, FlaskConical, Upload, Download, CheckCircle2, AlertCircle,
  ServerCog, RefreshCw, ExternalLink, Banknote,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/formatDate";

export default function Mftpf() {
  const { company } = useCompany();
  const cid = company?._id;
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [keypairResult, setKeypairResult] = useState(null);

  // Form state
  const [enabled, setEnabled] = useState(false);
  const [userId, setUserId] = useState("");
  const [efinanceNumber, setEfinanceNumber] = useState("");
  const [partnerNumber, setPartnerNumber] = useState("");
  const [host, setHost] = useState("mftp1.postfinance.ch");
  const [port, setPort] = useState(8022);
  const [testMode, setTestMode] = useState(true);
  const [pain001Version, setPain001Version] = useState("09");

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${cid}/mftpf-settings`);
      setSettings(r.data);
      setEnabled(r.data.enabled);
      setUserId(r.data.user_id || "");
      setEfinanceNumber(r.data.efinance_number || "");
      setPartnerNumber(r.data.partner_number || "");
      setHost(r.data.host || "mftp1.postfinance.ch");
      setPort(r.data.port || 8022);
      setTestMode(r.data.test_mode);
      setPain001Version(r.data.pain001_version || "09");
    } catch (e) {
      toast.error("Konnte Einstellungen nicht laden");
    } finally {
      setLoading(false);
    }
  }, [cid]);

  const loadJobs = useCallback(async () => {
    if (!cid) return;
    try {
      const r = await apiClient.get(`/companies/${cid}/mftpf/jobs`, { params: { limit: 20 } });
      setJobs(r.data || []);
    } catch (e) { /* silent */ }
  }, [cid]);

  useEffect(() => { load(); loadJobs(); }, [load, loadJobs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await apiClient.put(`/companies/${cid}/mftpf-settings`, {
        enabled, user_id: userId || null,
        efinance_number: efinanceNumber || null,
        partner_number: partnerNumber || null,
        host, port: Number(port), test_mode: testMode,
        pain001_version: pain001Version,
      });
      setSettings(r.data);
      toast.success("Einstellungen gespeichert.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (settings?.has_private_key) {
      if (!window.confirm("Es ist bereits ein Schlüsselpaar vorhanden. Neues generieren und ALTES überschreiben? Du musst den neuen Public Key dann erneut bei PostFinance hinterlegen!")) return;
    }
    setGenerating(true);
    try {
      const r = await apiClient.post(`/companies/${cid}/mftpf/generate-keypair`);
      setKeypairResult(r.data);
      toast.success("Schlüsselpaar generiert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generierung fehlgeschlagen");
    } finally { setGenerating(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await apiClient.post(`/companies/${cid}/mftpf/test-connection`);
      const d = r.data;
      if (d.success) {
        toast.success(`✅ ${d.mode === "mock" ? "Mock" : "Live"}: ${d.message}`);
      } else {
        toast.error(`Verbindungstest: ${d.message}`);
      }
      loadJobs();
    } catch (e) {
      toast.error("Verbindungstest fehlgeschlagen");
    } finally { setTesting(false); }
  };

  const copyKey = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("In Zwischenablage kopiert");
  };

  if (!cid) return <div className="p-6 text-slate-500">Bitte wähle eine Firma.</div>;
  if (loading) return <div className="p-6 text-slate-500">Lade …</div>;

  const fp = settings?.public_key_fingerprint;
  const isLiveReady = enabled && !testMode && settings?.has_private_key && userId;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="mftpf-page">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center">
            <ServerCog className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">PostFinance MFTPF</h1>
            <p className="text-sm text-slate-500 mt-1">
              Managed File Transfer über SFTP — gratis, ohne EBICS-Lizenz, mit RSA-4096-Public-Key.
            </p>
          </div>
        </div>
        {testMode ? (
          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">
            <FlaskConical className="h-3 w-3 mr-1" /> TEST-MODUS
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300">
            <CheckCircle2 className="h-3 w-3 mr-1" /> LIVE
          </Badge>
        )}
      </div>

      {/* Stammdaten */}
      <Card className="p-6 space-y-4" data-testid="mftpf-settings-card">
        <h3 className="font-semibold text-slate-900">Verbindungsdaten</h3>
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="mftpf-enabled-switch"
          />
          <Label className="text-sm">MFTPF-Connector aktivieren</Label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="user-id">MFTPF-User-ID *</Label>
            <Input
              id="user-id" value={userId} onChange={(e) => setUserId(e.target.value)}
              placeholder="z.B. CHE123456789"
              data-testid="mftpf-user-id-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">Wird von PostFinance bei Vertragsabschluss zugewiesen</p>
          </div>
          <div>
            <Label htmlFor="efinance">E-Finance-Nummer (Zahlungsverkehr)</Label>
            <Input
              id="efinance" value={efinanceNumber} onChange={(e) => setEfinanceNumber(e.target.value)}
              placeholder="30-12345-6"
              data-testid="mftpf-efinance-input"
            />
          </div>
          <div>
            <Label htmlFor="partner">Partnernummer (Billing Solutions)</Label>
            <Input
              id="partner" value={partnerNumber} onChange={(e) => setPartnerNumber(e.target.value)}
              data-testid="mftpf-partner-input"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label htmlFor="host">Host</Label>
              <Input id="host" value={host} onChange={(e) => setHost(e.target.value)} data-testid="mftpf-host-input" />
            </div>
            <div>
              <Label htmlFor="port">Port</Label>
              <Input id="port" type="number" value={port} onChange={(e) => setPort(e.target.value)} data-testid="mftpf-port-input" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
          <Switch
            checked={testMode}
            onCheckedChange={setTestMode}
            data-testid="mftpf-testmode-switch"
          />
          <Label className="text-sm">
            <FlaskConical className="h-3 w-3 inline mr-1 text-amber-600" />
            Test-Modus (kein realer SFTP-Connect, Mock-Antworten)
          </Label>
        </div>

        {/* pain.001 Version (Iter 110b) */}
        <div className="pt-3 border-t border-slate-100 space-y-1.5">
          <Label className="text-sm font-medium" htmlFor="pain001-version">
            pain.001-XML Version (Zahlungsaufträge)
          </Label>
          <select
            id="pain001-version"
            value={pain001Version}
            onChange={(e) => setPain001Version(e.target.value)}
            className="block w-full max-w-md rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
            data-testid="mftpf-pain001-version-select"
          >
            <option value="09">pain.001.001.09 (CGI Worldwide · Standard 2022+) — empfohlen</option>
            <option value="03">pain.001.001.03 (CH Legacy · PostFinance Handbuch 499.79)</option>
          </select>
          <p className="text-[11px] text-slate-500">
            PostFinance akzeptiert in der Praxis beide Versionen. Falls TSCorp explizit
            Version <strong>.03</strong> verlangt, hier umschalten. Default <strong>.09</strong>
            ist die international gebräuchliche CGI-Norm.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={load} disabled={saving}>Verwerfen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="mftpf-save-btn">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </Card>

      {/* SSH-Schlüsselpaar */}
      <Card className="p-6 space-y-4" data-testid="mftpf-keys-card">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-violet-600" />
            SSH-Schlüsselpaar
          </h3>
          {settings?.has_private_key ? (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="h-3 w-3 mr-1" /> hinterlegt
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-50 text-amber-700">
              <AlertCircle className="h-3 w-3 mr-1" /> nicht hinterlegt
            </Badge>
          )}
        </div>

        <p className="text-sm text-slate-600">
          PostFinance MFTPF nutzt Public-Key-Authentifizierung (RSA ≥ 4096 Bit).
          Generiere hier ein Schlüsselpaar — der Private Key bleibt verschlüsselt im System,
          den Public Key musst du bei PostFinance hinterlegen.
        </p>

        {settings?.public_key && (
          <div className="space-y-2">
            <Label>Public Key (zum Hochladen bei PostFinance)</Label>
            <div className="flex gap-2">
              <textarea
                readOnly
                value={settings.public_key}
                className="flex-1 font-mono text-[11px] p-2 border rounded bg-slate-50 h-20"
                data-testid="mftpf-public-key-display"
              />
              <Button variant="outline" size="sm" onClick={() => copyKey(settings.public_key)}>
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            {fp && (
              <p className="text-[11px] text-slate-500 font-mono">Fingerprint: {fp}</p>
            )}
          </div>
        )}

        <Button
          onClick={handleGenerate}
          disabled={generating}
          variant="outline"
          data-testid="mftpf-generate-btn"
        >
          <KeyRound className="h-4 w-4 mr-2" />
          {generating ? "Generiere…" : settings?.has_private_key ? "Neues Schlüsselpaar generieren (überschreibt altes!)" : "Schlüsselpaar generieren"}
        </Button>

        {keypairResult && (
          <div className="mt-3 p-3 rounded border border-violet-200 bg-violet-50/50 text-xs text-slate-700">
            <p className="font-semibold mb-1">📝 Nächste Schritte:</p>
            <pre className="whitespace-pre-wrap">{keypairResult.instructions}</pre>
          </div>
        )}
      </Card>

      {/* Aktionen */}
      <Card className="p-6 space-y-3" data-testid="mftpf-actions-card">
        <h3 className="font-semibold text-slate-900">Aktionen</h3>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleTest}
            disabled={testing || !enabled}
            data-testid="mftpf-test-btn"
          >
            <FlaskConical className="h-4 w-4 mr-2" />
            {testing ? "Teste…" : "Verbindung testen"}
          </Button>
          <Button
            variant="outline"
            disabled={!enabled}
            onClick={async () => {
              try {
                const r = await apiClient.post(`/companies/${cid}/mftpf/download`, { kind: "yellow_net_reports" });
                toast.success(`${r.data.files.length} Datei(en) heruntergeladen`);
                loadJobs();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Download fehlgeschlagen");
              }
            }}
            data-testid="mftpf-download-camt-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            camt.053 herunterladen
          </Button>
          {/* Iter109: pain.002 Status-Sync */}
          <Button
            variant="outline"
            disabled={!enabled}
            onClick={async () => {
              try {
                const r = await apiClient.post(`/companies/${cid}/mftpf/sync-pain002`);
                const d = r.data;
                if (d.transactions_total === 0) {
                  toast.info("Keine pain.002-Dateien gefunden");
                } else {
                  toast.success(
                    `${d.files_processed} pain.002 verarbeitet · ${d.bills_settled} gezahlt · ${d.bills_rejected} abgelehnt`,
                  );
                }
                loadJobs();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Status-Sync fehlgeschlagen");
              }
            }}
            data-testid="mftpf-sync-pain002-btn"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Zahlungs-Status synchronisieren
          </Button>
          {/* Iter109b: camt.054 Kundenzahlungen synchronisieren */}
          <Button
            variant="outline"
            disabled={!enabled}
            onClick={async () => {
              try {
                const r = await apiClient.post(`/companies/${cid}/mftpf/sync-camt054`);
                const d = r.data;
                if (d.files_processed === 0) {
                  toast.info("Keine camt.054-Dateien gefunden");
                } else {
                  toast.success(
                    `${d.files_processed} camt.054 verarbeitet · ${d.invoices_marked_paid} Rechnung(en) gezahlt · ${d.unmatched_count} ungemacht`,
                  );
                }
                loadJobs();
              } catch (e) {
                toast.error(e.response?.data?.detail || "camt.054-Sync fehlgeschlagen");
              }
            }}
            data-testid="mftpf-sync-camt054-btn"
            className="border-sky-300 text-sky-700 hover:bg-sky-50"
          >
            <Banknote className="h-4 w-4 mr-2" />
            Kundenzahlungen synchronisieren (camt.054)
          </Button>
        </div>

        {!isLiveReady && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2 items-start">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Für Live-Betrieb: 1) User-ID eintragen, 2) Schlüsselpaar generieren,
              3) Public Key bei PostFinance hinterlegen (24-48h Aktivierungszeit), 4) Test-Modus deaktivieren.
            </span>
          </div>
        )}
      </Card>

      {/* Job-Log */}
      <Card className="overflow-hidden" data-testid="mftpf-jobs-card">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Letzte Aktionen</h3>
          <Button variant="ghost" size="sm" onClick={loadJobs}>
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 border-b">
              <tr>
                <th className="px-3 py-2">Zeit</th>
                <th className="px-3 py-2">Typ</th>
                <th className="px-3 py-2">Verzeichnis</th>
                <th className="px-3 py-2">Datei(en)</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Modus</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-6 text-slate-400">Noch keine Jobs</td></tr>
              ) : jobs.map((j) => (
                <tr key={j.id} className="border-b hover:bg-slate-50" data-testid={`mftpf-job-${j.id}`}>
                  <td className="px-3 py-2 text-xs text-slate-500">{formatDate(j.run_at)} {(j.run_at || "").slice(11, 16)}</td>
                  <td className="px-3 py-2">
                    {j.job_type === "upload" ? <Upload className="h-3 w-3 inline mr-1 text-violet-600" /> : <Download className="h-3 w-3 inline mr-1 text-emerald-600" />}
                    {j.job_type}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">{j.kind}</td>
                  <td className="px-3 py-2 text-xs">{j.filename || `${j.file_count || 0} Datei(en)`}</td>
                  <td className="px-3 py-2">
                    {j.success ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700">OK</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-rose-50 text-rose-700">Fehler</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs uppercase">{j.mode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="text-xs text-slate-500 flex items-center gap-1">
        <ExternalLink className="h-3 w-3" />
        Doku: <a href="https://www.postfinance.ch/de/business/produkte/dl-channel/mftpf.html" target="_blank" rel="noreferrer" className="text-violet-600 underline">PostFinance MFTPF</a>
      </div>
    </div>
  );
}
